import { useState, useRef, useCallback } from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Progress } from "@shared/components/ui/progress";
import { Badge } from "@shared/components/ui/badge";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  X,
  Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

const ImportSteps = {
  SELECT: "select",
  VALIDATE: "validate",
  UPLOAD: "upload",
  COMPLETE: "complete",
};

export function EnhancedImportFile({
  template,
  handleFileChange,
  handleUpload,
  isPending = false,
  onFileValidation,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(ImportSteps.SELECT);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [errors, setErrors] = useState([]);

  const validateFile = useCallback(
    (file) => {
      const errors = [];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!file) {
        errors.push(t("import.errors.noFileSelected"));
        return { isValid: false, errors };
      }

      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        errors.push(t("import.errors.invalidFileType"));
      }

      if (file.size > maxSize) {
        errors.push(t("import.errors.fileTooLarge"));
      }

      if (file.size === 0) {
        errors.push(t("import.errors.emptyFile"));
      }

      return {
        isValid: errors.length === 0,
        errors,
        fileInfo: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: new Date(file.lastModified),
        },
      };
    },
    [t]
  );

  const handleFileSelection = useCallback(
    (file) => {
      const validation = validateFile(file);
      setValidationResults(validation);
      setErrors(validation.errors);

      if (validation.isValid) {
        setSelectedFile(file);
        setCurrentStep(ImportSteps.VALIDATE);
        handleFileChange({ target: { files: [file] } });
        if (onFileValidation) {
          onFileValidation(validation);
        }
      } else {
        setSelectedFile(null);
        setCurrentStep(ImportSteps.SELECT);
      }
    },
    [handleFileChange, onFileValidation, validateFile]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelection(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelection]
  );

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setCurrentStep(ImportSteps.SELECT);
    setUploadProgress(0);
    setValidationResults(null);
    setErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    setCurrentStep(ImportSteps.UPLOAD);
    setUploadProgress(0);

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await handleUpload();
      setUploadProgress(100);
      setCurrentStep(ImportSteps.COMPLETE);
    } catch (error) {
      setErrors([error.message || t("import.errors.uploadFailed")]);
      setCurrentStep(ImportSteps.VALIDATE);
    } finally {
      clearInterval(progressInterval);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          {t("import.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Steps */}
    {/*    <div className="flex items-center justify-between">
          {Object.values(ImportSteps).map((step, index) => (
            <div key={step} className="flex items-center">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  currentStep === step
                    ? "bg-primary text-primary-foreground"
                    : Object.values(ImportSteps).indexOf(currentStep) > index
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {Object.values(ImportSteps).indexOf(currentStep) > index ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>
              {index < Object.values(ImportSteps).length - 1 && (
                <div
                  className={cn(
                    "h-0.5 w-12 mx-2",
                    Object.values(ImportSteps).indexOf(currentStep) > index
                      ? "bg-green-500"
                      : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div> */}

        {/* Error Display */}
        {errors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* File Upload Area */}
        {currentStep === ImportSteps.SELECT && (
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              dragActive
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <Upload className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-medium">{t("import.dragDrop")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("import.dragDropDescription")}
                </p>
              </div>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="relative"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {t("import.chooseFile")}
                </Button>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {t("import.supportedFormats")}: .xlsx, .xls (Max 10MB)
              </div>
            </div>
          </div>
        )}

        {/* File Validation Display */}
        {currentStep === ImportSteps.VALIDATE &&
          selectedFile &&
          validationResults && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <FileSpreadsheet className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{selectedFile.name}</h4>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{formatFileSize(selectedFile.size)}</span>
                          <Badge variant="secondary">{t("import.ready")}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={resetUpload}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button
                  onClick={handleUploadClick}
                  disabled={isPending}
                  className="flex-1"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("import.uploading")}
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      {t("import.startImport")}
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={resetUpload}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          )}

        {/* Upload Progress */}
        {currentStep === ImportSteps.UPLOAD && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="font-medium">{t("import.processing")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("import.processingDescription")}
              </p>
            </div>
            <Progress value={uploadProgress} className="w-full" />
            <div className="text-center text-sm text-muted-foreground">
              {uploadProgress}% {t("import.complete")}
            </div>
          </div>
        )}

        {/* Success State */}
        {currentStep === ImportSteps.COMPLETE && (
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-green-800">
                {t("import.success")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("import.successDescription")}
              </p>
            </div>
            <Button onClick={resetUpload} variant="outline">
              {t("import.importAnother")}
            </Button>
          </div>
        )}

        {/* Template Download */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-sm">
                {t("import.needTemplate")}
              </h4>
              <p className="text-xs text-muted-foreground">
                {t("import.templateDescription")}
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a
                href={template.url}
                download={template.name}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {t("import.download")}
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
